import { useMemo } from "react";
import { useRecoilValue } from "recoil";

import {
  buildTrackingRouteForRide,
  editRideTrackingAF,
  getRideRouteInfo,
  ILocationInfoBase,
  IRideDto,
  IRideRoute,
  Log,
} from "@ryde/shared";

export const useRideRoutes = (rideDto: IRideDto | undefined) => {
  // Reads the single-ride edit/detail tracking store, NOT the shared live-map store (rideTrackingAF).
  // The live-map poll rewrites the shared store with only the last GPS point (lastTrackingOnly), which
  // would wipe this view's full trail on every poll. Keep this on editRideTrackingAF — not a typo.
  const trackingDtos = useRecoilValue(editRideTrackingAF(rideDto?.id || 0));
  const rideRoute = useMemo(
    () => (rideDto ? getRideRouteInfo(rideDto) : undefined),
    [rideDto],
  );

  const locationInfos: ILocationInfoBase[] = useMemo(
    () => (rideRoute?.locations ? rideRoute.locations : []),
    [rideRoute?.locations],
  );

  const trackingRoute: IRideRoute | undefined = useMemo(() => {
    if (!rideDto) {
      return undefined;
    }

    Log.debug("Tracking locations applied to map:", trackingDtos);

    return buildTrackingRouteForRide({ rideDto, trackingDtos });
  }, [rideDto, trackingDtos]);

  const rideRoutes = useMemo(() => (rideRoute ? [rideRoute] : []), [rideRoute]);
  const trackingRoutes = useMemo(
    () => (trackingRoute ? [trackingRoute] : []),
    [trackingRoute],
  );

  return {
    trackingRoute,
    trackingRoutes,
    locationInfos,
    rideRoute,
    rideRoutes,
  };
};
